import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AnalyticsData } from '../../services/socialMediaAnalytics';

interface Props {
  data: AnalyticsData;
  platform: string;
}

const AnalyticsCharts: React.FC<Props> = ({ data, platform }) => {
  return (
    <Grid container spacing={3}>
      {/* Crescimento de Seguidores */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Crescimento de Seguidores
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.followers.history}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date: string) => format(new Date(date), 'dd/MM', { locale: ptBR })}
                  />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#8884d8"
                    name="Seguidores"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Taxa de Engajamento */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Taxa de Engajamento
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.engagement.history}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date"
                    tickFormatter={(date: string) => format(new Date(date), 'dd/MM', { locale: ptBR })}
                  />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    stroke="#82ca9d"
                    name="Engajamento %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Melhores Horários */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Melhores Horários para Postagem
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.engagement.bestTimes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="hour"
                    tickFormatter={(hour: number) => `${hour}h`}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="engagement"
                    fill="#8884d8"
                    name="Engajamento %"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Análise de Sentimento */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Análise de Sentimento
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Positivo
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={data.sentiment.positive} 
                color="success"
                sx={{ mb: 1, height: 10, borderRadius: 5 }}
              />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Neutro
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={data.sentiment.neutral} 
                color="primary"
                sx={{ mb: 1, height: 10, borderRadius: 5 }}
              />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Negativo
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={data.sentiment.negative} 
                color="error"
                sx={{ mb: 1, height: 10, borderRadius: 5 }}
              />
            </Box>
            <Typography variant="subtitle2" gutterBottom>
              Palavras-chave mais mencionadas:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {data.sentiment.keywords.map((keyword) => (
                <Chip
                  key={keyword.word}
                  label={`${keyword.word} (${keyword.count})`}
                  size="small"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default AnalyticsCharts;
